import AsyncStorage from '@react-native-async-storage/async-storage';

class ApiClient {
  constructor() {
    this.baseURL = 'https://api.jobeng.ai';  // FIXED: Added :3000 port
    this.mockMode = false;
  }

  setMockMode(enabled) {
    this.mockMode = enabled;
    console.log(`Mock mode ${enabled ? 'enabled' : 'disabled'}`);
  }

  async testConnection() {
    try {
      console.log('Testing backend connection...');
      
      // Try multiple health check endpoints
      const healthEndpoints = [
        '/health',               // Main health endpoint
        '/api/auth/test',        // Try auth test endpoint
        '/api/users/me',         // Try user endpoint (might return 401 but means server is up)
        '/api/status',           // Common status endpoint
        '/api/ping',             // Common ping endpoint
        '/',                     // Root endpoint
      ];

      for (const endpoint of healthEndpoints) {
        try {
          console.log(`Trying health check: ${this.baseURL}${endpoint}`);
          
          const response = await fetch(`${this.baseURL}${endpoint}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
          'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            },
            timeout: 5000, // 5 second timeout
          });

          // If we get any response (even 401, 404), the server is up
          if (response.status < 500) {
            console.log(`Backend is available (${response.status} from ${endpoint})`);
            return true;
          }
        } catch (error) {
          console.log(`Health check failed for ${endpoint}:`, error.message);
          continue;
        }
      }

      console.log('All health check endpoints failed');
      return false;
    } catch (error) {
      console.error('Connection test error:', error);
      return false;
    }
  }

  async request(endpoint, options = {}) {
    if (this.mockMode) {
      return this.mockRequest(endpoint, options);
    }

    try {
      const url = `${this.baseURL}${endpoint}`;
      console.log(`Making ${options.method || 'GET'} request to: ${url}`);

      const token = await AsyncStorage.getItem('auth_token');
      
      const config = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        ...options,
      };

      if (options.body && typeof options.body === 'object') {
        config.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      return {
        success: true,
        data: data,
        status: response.status,
      };
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  async login(credentials) {
    if (this.mockMode) {
      return this.mockLogin(credentials);
    }

    return this.request('/api/auth/login', {
      method: 'POST',
      body: credentials,
    });
  }

  async register(userData) {
    if (this.mockMode) {
      return this.mockRegister(userData);
    }

    return this.request('/api/auth/register', {
      method: 'POST',
      body: userData,
    });
  }

  async getProfile() {
    if (this.mockMode) {
      return this.mockGetProfile();
    }

    return this.request('/api/users/profile');
  }

  async updateProfile(profileData) {
    if (this.mockMode) {
      return this.mockUpdateProfile(profileData);
    }

    return this.request('/api/users/profile', {
      method: 'PUT',
      body: profileData,
    });
  }

  // Mock implementations
  async mockRequest(endpoint, options = {}) {
    console.log(`Mock request: ${options.method || 'GET'} ${endpoint}`);
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    if (endpoint.includes('/auth/login')) {
      return this.mockLogin(options.body);
    } else if (endpoint.includes('/auth/register')) {
      return this.mockRegister(options.body);
    } else if (endpoint.includes('/users/profile')) {
      if (options.method === 'PUT') {
        return this.mockUpdateProfile(options.body);
      } else {
        return this.mockGetProfile();
      }
    }

    return {
      success: true,
      data: { message: 'Mock response' },
      status: 200,
    };
  }

  async mockLogin(credentials) {
    const { email, password } = credentials;

    if (password.length < 6) {
      throw new Error('Invalid credentials');
    }

    // Determine user type based on email
    let userType = 'professional';
    if (email.includes('business')) {
      userType = 'business';
    } else if (email.includes('learning')) {
      userType = 'learning';
    }

    const userData = {
      id: 'mock_user_123',
      email: email,
      full_name: 'Demo User',
      user_type: userType,
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400',
      phone: '+1234567890',
      location: 'Demo City',
      about: 'This is a demo user for testing purposes.',
    };

    const token = 'mock_jwt_token_12345';

    return {
      success: true,
      data: {
        user: userData,
        token: token,
      },
      status: 200,
    };
  }

  async mockRegister(userData) {
    const mockUser = {
      id: 'mock_user_' + Date.now(),
      email: userData.email,
      full_name: userData.full_name || userData.name,
      user_type: userData.user_type || 'professional',
      avatar: '',
      phone: userData.phone || '',
      location: userData.location || '',
      about: userData.about || '',
      ...userData,
    };

    const token = 'mock_jwt_token_' + Date.now();

    return {
      success: true,
      data: {
        user: mockUser,
        token: token,
      },
      status: 201,
    };
  }

  async mockGetProfile() {
    const savedProfile = await AsyncStorage.getItem('mock_profile');
    
    const defaultProfile = {
      id: 'mock_user_123',
      email: 'demo@example.com',
      full_name: 'Demo User',
      user_type: 'professional',
      avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400',
      phone: '+1234567890',
      location: 'Demo City',
      about: 'This is a demo user for testing purposes.',
    };

    const profile = savedProfile ? JSON.parse(savedProfile) : defaultProfile;

    return {
      success: true,
      data: {
        user: profile,
      },
      status: 200,
    };
  }

  async mockUpdateProfile(profileData) {
    // Save to AsyncStorage for persistence in mock mode
    await AsyncStorage.setItem('mock_profile', JSON.stringify(profileData));

    return {
      success: true,
      data: {
        user: profileData,
      },
      status: 200,
    };
  }
}

export default new ApiClient();
